import { useEffect, useRef, useState } from "react";
import type { Video } from "@/mocks/videos";
import { formatDuration } from "@/lib/video";

type Status = "idle" | "loading" | "playing" | "paused" | "error";

export default function VideoPlayer({ video }: { video: Video }) {
  const [status, setStatus] = useState<Status>("idle");
  const [currentTime, setCurrentTime] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const loadTimer = useRef<number | null>(null);
  const tickTimer = useRef<number | null>(null);

  const clearTimers = () => {
    if (loadTimer.current) window.clearTimeout(loadTimer.current);
    if (tickTimer.current) window.clearInterval(tickTimer.current);
    loadTimer.current = null;
    tickTimer.current = null;
  };

  useEffect(() => {
    clearTimers();
    setStatus("idle");
    setCurrentTime(0);
    return clearTimers;
  }, [video.id]);

  useEffect(() => {
    if (status !== "playing") return undefined;
    tickTimer.current = window.setInterval(() => {
      setCurrentTime((t) => {
        if (t + 1 >= video.duration) {
          setStatus("paused");
          return video.duration;
        }
        return t + 1;
      });
    }, 1000);
    return () => {
      if (tickTimer.current) window.clearInterval(tickTimer.current);
    };
  }, [status, video.duration]);

  const startPlayback = () => {
    setStatus("loading");
    clearTimers();
    loadTimer.current = window.setTimeout(() => {
      setStatus("playing");
    }, 1200);
  };

  const togglePlay = () => {
    if (status === "playing") setStatus("paused");
    else if (status === "paused" || status === "idle") startPlayback();
    else if (status === "error") startPlayback();
  };

  const triggerError = () => {
    clearTimers();
    setStatus("error");
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setCurrentTime(Math.round(ratio * video.duration));
  };

  const goFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  const progress = Math.min(100, (currentTime / video.duration) * 100);

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-lg bg-foreground-950"
    >
      <div className="relative aspect-video w-full">
        <img
          src={video.cover}
          alt={`${video.title} - 日批视频播放器`}
          title={`${video.title} - 日批视频`}
          className={`h-full w-full object-cover transition-all duration-500 ${
            status === "playing" ? "scale-105 opacity-90" : "opacity-100"
          }`}
        />

        {(status === "idle" || status === "paused") && (
          <div className="absolute inset-0 bg-foreground-950/30"></div>
        )}

        {status === "playing" && (
          <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-foreground-950/70 px-3 py-1 text-xs font-label text-background-50">
            <span className="h-2 w-2 animate-pulse rounded-full bg-primary-500"></span>
            演示播放中
          </div>
        )}

        {(status === "idle" || status === "paused") && (
          <button
            type="button"
            onClick={togglePlay}
            aria-label="播放"
            className="absolute inset-0 flex items-center justify-center cursor-pointer"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors duration-200 hover:bg-primary-600 md:h-20 md:w-20">
              <i className="ri-play-fill text-3xl md:text-4xl"></i>
            </span>
          </button>
        )}

        {status === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-foreground-950/60">
            <span className="h-10 w-10 animate-spin rounded-full border-2 border-background-100/40 border-t-primary-500"></span>
            <p className="text-sm font-label text-background-100">视频加载中…</p>
          </div>
        )}

        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-foreground-950/80 px-6 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500/20 text-primary-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </span>
            <p className="font-label text-background-50">视频加载失败</p>
            <p className="max-w-xs text-xs text-background-200">
              演示环境暂不支持真实播放流，请点击重试查看加载状态演示。
            </p>
            <button
              type="button"
              onClick={startPlayback}
              className="mt-1 inline-flex items-center gap-1 rounded-md bg-primary-500 px-4 py-2 text-sm font-label text-background-50 transition-colors duration-200 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line text-lg"></i>
              重试
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 bg-foreground-950 px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={status === "playing" ? "暂停" : "播放"}
            className="flex h-9 w-9 items-center justify-center rounded-full text-background-50 transition-colors duration-200 hover:bg-background-50/10 cursor-pointer"
          >
            <i className={`${status === "playing" ? "ri-pause-fill" : "ri-play-fill"} text-xl`}></i>
          </button>
          <span className="font-label text-xs text-background-200">
            {formatDuration(currentTime)} / {formatDuration(video.duration)}
          </span>
          <div
            onClick={seek}
            className="group ml-2 h-1.5 flex-1 cursor-pointer rounded-full bg-background-100/20"
          >
            <div
              className="relative h-full rounded-full bg-primary-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            >
              <span className="absolute right-0 top-1/2 h-3 w-3 -translate-y-1/2 translate-x-1/2 rounded-full bg-primary-500"></span>
            </div>
          </div>
          <button
            type="button"
            aria-label="音量"
            className="hidden h-9 w-9 items-center justify-center rounded-full text-background-50 transition-colors duration-200 hover:bg-background-50/10 sm:flex cursor-pointer"
          >
            <i className="ri-volume-up-line text-xl"></i>
          </button>
          <button
            type="button"
            onClick={goFullscreen}
            aria-label="全屏"
            className="flex h-9 w-9 items-center justify-center rounded-full text-background-50 transition-colors duration-200 hover:bg-background-50/10 cursor-pointer"
          >
            <i className="ri-fullscreen-line text-xl"></i>
          </button>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-background-300">
            当前为前端演示播放器，无真实视频流
          </p>
          <button
            type="button"
            onClick={triggerError}
            className="text-xs font-label text-background-300 underline-offset-2 transition-colors duration-200 hover:text-primary-400 hover:underline cursor-pointer whitespace-nowrap"
          >
            模拟加载失败
          </button>
        </div>
      </div>
    </div>
  );
}